import { useMemo } from "react"
import { Avatar, Box, Chip, Typography, useTheme } from "@mui/material"
import { useTranslation } from "providers/i18n"
import { prepareSearchResultSecondaryText } from "utils/search"
import { getIcon } from "utils/icon"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { FeatureListItem } from "../features/ListItem"
import { ShopIcon } from "components/icons"
import {
  excludeVatRefundCategory,
  hasVatRefund,
} from "extensions/iconsiam/utils"
import { ComingSoonChip, NewShopChip, VatRefundChip } from "../../chips"

export const SearchListItem = ({
  data,
  primaryTypographyProps = {},
  secondaryTypographyProps = {},
  endAdornmentSlotClasses = {},
  endAdornmentSlots = {},
  ...props
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const {
    categoryText: rawCategoryText,
    zoneText,
    boothNameText,
    groupText,
    avatarProps,
    venueRef,
    isAd = false,
    name,
    locationDescriptionText,
    isNewShop = false,
    isComingSoon = false,
  } = data
  const theme = useTheme()

  const { venueName, allLevels } = useMemo(
    () => getFeatureDetail(data),
    [data]
  ) as IOccupantDetail

  const isVatRefund = useMemo(() => hasVatRefund(data), [data])

  const { styleOverrides = {}, defaultProps = {} } =
    theme.components[`VuiSearchResultItem`] || {}
  const {
    chip,
    primaryWrapper,
    chipContainer,
    comingSoonChip,
    newShopChip,
    vatRefundChip,
  } = styleOverrides

  const primaryText = t(name)
  const venueText = t(venueName ?? "")

  // Only re-derive the category when VAT Refund is present, so the shown
  // category excludes it; otherwise keep the parser's original categoryText.
  const categoryText = useMemo(() => {
    if (!isVatRefund) return rawCategoryText
    const { category } = getFeatureDetail(
      excludeVatRefundCategory(data)
    ) as IOccupantDetail
    return category && t(category)
  }, [data, isVatRefund, rawCategoryText, t])

  const allLevelDisplay = useMemo(
    () => allLevels?.join(", ") || "-",
    [allLevels]
  )

  const { secondaryTextKeys } = defaultProps

  const secondaryText = useMemo(
    () =>
      prepareSearchResultSecondaryText(
        categoryText,
        boothNameText,
        allLevelDisplay,
        zoneText,
        groupText,
        locationDescriptionText,
        secondaryTextKeys,
        venueText
      ),
    [
      categoryText,
      boothNameText,
      allLevelDisplay,
      zoneText,
      groupText,
      locationDescriptionText,
      secondaryTextKeys,
      venueText,
    ]
  )

  return (
    <FeatureListItem
      divider
      disableTypography
      className={venueRef}
      startAdornment={
        <Box sx={chipContainer}>
          {isComingSoon && !isAd && (
            <ComingSoonChip sx={comingSoonChip} label={t("Coming soon")} />
          )}
          {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
          {isVatRefund && <VatRefundChip sx={vatRefundChip} />}
        </Box>
      }
      primary={
        <Box sx={primaryWrapper}>
          <Typography
            variant="h6"
            className="MuiListItemText-primary"
            {...primaryTypographyProps}
          >
            {primaryText}
          </Typography>
          {isAd && (
            <Chip
              variant="filled"
              label={t("sponsored.search_result")}
              sx={chip}
            />
          )}
        </Box>
      }
      secondary={
        <Typography
          variant="subtitle1"
          className="MuiListItemText-secondary"
          {...secondaryTypographyProps}
        >
          {secondaryText}
        </Typography>
      }
      avatar={
        <Avatar src={avatarProps.src} variant="square">
          {getIcon(avatarProps.icon, null, {
            imageIconProps: {
              solidColorFilterId: "searchListItem_avatar_filter",
            },
          }) || <ShopIcon />}
        </Avatar>
      }
      endAdornmentSlotClasses={endAdornmentSlotClasses}
      endAdornmentSlots={endAdornmentSlots}
      {...props}
    />
  )
}
